import React, { useEffect, useRef, useState } from 'react';
import { BaseButton } from '@repo/common';
import { useGoogleMap } from '../hooks/useGoogleMap';

const getAddressComponent = (components, type) =>
  components?.find((component) => component.types.includes(type))?.long_name ||
  '';

const getListingLocation = (components, fallbackAddress) => {
  const district =
    getAddressComponent(components, 'sublocality_level_1') ||
    getAddressComponent(components, 'locality');
  const city =
    getAddressComponent(components, 'locality') ||
    getAddressComponent(components, 'administrative_area_level_1');

  return [...new Set([district, city].filter(Boolean))].join(', ') ||
    fallbackAddress;
};

const MeetingPointPicker = ({
  meetingPoint,
  onSelect,
  onClear,
  errors = {},
}) => {
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const selectedQueryRef = useRef('');
  const rootRef = useRef(null);
  const { mapRef, mapError } = useGoogleMap(
    meetingPoint.address,
    coordinates,
  );

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setPredictions([]);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (
      normalizedQuery.length < 2 ||
      normalizedQuery === selectedQueryRef.current
    ) {
      setPredictions([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      if (!window.google?.maps?.places) {
        setSearchError('Google Places is not available.');
        return;
      }

      setLoading(true);
      setSearchError('');
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: normalizedQuery,
          componentRestrictions: { country: 'kr' },
          language: 'en',
        },
        (results, status) => {
          setLoading(false);
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results
          ) {
            setPredictions(results.slice(0, 6));
            return;
          }
          setPredictions([]);
          if (
            status !==
            window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            setSearchError('Could not search for that place.');
          }
        },
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleQueryChange = (event) => {
    selectedQueryRef.current = '';
    setQuery(event.target.value);
    setSearchError('');
    setCoordinates(null);
    if (meetingPoint.address) onClear();
  };

  const selectPrediction = (prediction) => {
    if (!window.google?.maps?.places) return;

    setLoading(true);
    const service = new window.google.maps.places.PlacesService(
      document.createElement('div'),
    );
    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: [
          'name',
          'formatted_address',
          'address_components',
          'geometry',
          'place_id',
        ],
      },
      (place, status) => {
        setLoading(false);
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place?.formatted_address
        ) {
          setSearchError('Could not load the selected place.');
          return;
        }

        selectedQueryRef.current = prediction.description;
        setQuery(prediction.description);
        setPredictions([]);
        setCoordinates({
          lat: place.geometry?.location?.lat(),
          lng: place.geometry?.location?.lng(),
        });
        onSelect({
          placeId: place.place_id,
          name: place.name || prediction.structured_formatting?.main_text || '',
          address: place.formatted_address,
          location: getListingLocation(
            place.address_components,
            place.formatted_address,
          ),
          latitude: place.geometry?.location?.lat(),
          longitude: place.geometry?.location?.lng(),
        });
      },
    );
  };

  return (
    <div ref={rootRef} className='flex flex-col gap-5'>
      <div className='relative'>
        <label className='mb-2 block typo-b14Strong text-text-primary'>
          Search meeting point *
        </label>
        <input
          value={query}
          onChange={handleQueryChange}
          autoComplete='off'
          placeholder='Search a building, station exit, or street address'
          className='w-full rounded-lg border border-grayscale-gray03 bg-white px-4 py-3 typo-b14 text-text-primary outline-none transition focus:border-green-deep02'
        />

        {(loading || predictions.length > 0) && (
          <div className='absolute left-0 top-full z-30 mt-1 w-full overflow-hidden rounded-xl border border-grayscale-gray02 bg-white p-2 shadow-popup-02'>
            {loading ? (
              <div className='flex h-20 items-center justify-center'>
                <div className='size-7 animate-spin rounded-full border-[3px] border-grayscale-gray02 border-t-green-deep02' />
              </div>
            ) : (
              predictions.map((prediction) => (
                <BaseButton
                  key={prediction.place_id}
                  type='text'
                  size='m'
                  htmlType='button'
                  hideIcon
                  typo={false}
                  isStatic
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectPrediction(prediction)}
                  className='h-auto w-full min-w-0 cursor-pointer flex-col rounded-lg border-0 bg-transparent px-3 py-2 text-left hover:bg-grayscale-gray01'
                  textWrapperClass='flex h-auto w-full flex-col items-start'
                >
                  <span className='typo-b14Strong text-text-primary'>
                    {prediction.structured_formatting?.main_text}
                  </span>
                  <span className='typo-b12 text-text-secondary'>
                    {prediction.structured_formatting?.secondary_text}
                  </span>
                </BaseButton>
              ))
            )}
          </div>
        )}

        {(searchError || errors.meetingPoint) && (
          <p className='mt-1 typo-b12 text-red-600'>
            {searchError || errors.meetingPoint}
          </p>
        )}
      </div>

      {meetingPoint.address && (
        <>
          <div className='grid grid-cols-2 gap-5 rounded-xl bg-grayscale-gray01 p-5'>
            <div>
              <p className='typo-b12Strong text-text-secondary'>
                Selected place
              </p>
              <p className='mt-1 typo-b14Strong text-text-primary'>
                {meetingPoint.name}
              </p>
            </div>
            <div>
              <p className='typo-b12Strong text-text-secondary'>
                Full address
              </p>
              <p className='mt-1 typo-b14 text-text-primary'>
                {meetingPoint.address}
              </p>
            </div>
          </div>

          <div className='overflow-hidden rounded-xl border border-grayscale-gray02'>
            {mapError ? (
              <div className='flex h-[300px] items-center justify-center bg-grayscale-gray01'>
                <p className='typo-b14 text-text-secondary'>{mapError}</p>
              </div>
            ) : (
              <div ref={mapRef} className='h-[300px] w-full' />
            )}
          </div>
          <p className='typo-b12 text-text-secondary'>
            The pin preview uses the selected Google place. Add arrival details
            below if guests need a specific exit, floor, or landmark.
          </p>
        </>
      )}
    </div>
  );
};

export default MeetingPointPicker;
